import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Undo2, Trash2, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { Seo } from "@/components/Seo";
import { Pizza, ToppingIcon, PIZZA_INGREDIENTS, MAX_PER_SLICE } from "@/components/game/Pizza";
import { ShareButtons } from "@/components/game/share";
import { GameOffer } from "@/components/game/GameOffer";
import { SaveToGallery } from "@/components/game/SaveToGallery";
import { sfx, SoundToggle } from "@/components/game/sound";
import { pageJsonld, PAGES } from "@/seo.mjs";
import MENUS from "@/game/menus.json";

const FREE_KEYS = ["conozco_mis_ingredientes", "abrimos_la_pizzeria", "pizza_para_pintar"];
const moments = MENUS.pizza.map((m) => {
  const target = FREE_KEYS.includes(m.key) ? null : Object.fromEntries(m.ingredients.map((s) => {
    const [name, qty] = s.split(" × ");
    return [name.trim(), qty ? parseInt(qty, 10) : 1];
  }));
  return { ...m, target };
});
const empty = () => [[], [], [], []];

export default function PizzaGame() {
  const { t } = useLang();
  const g = t.game, z = t.pizza;
  const svgRef = useRef(null);
  const [slices, setSlices] = useState(empty());
  const [ing, setIng] = useState("Pepperoni");
  const [key, setKey] = useState(null);
  const [hover, setHover] = useState(null);
  const [history, setHistory] = useState([]);
  const moment = moments.find((m) => m.key === key);
  const counts = slices.flat().reduce((a, k) => ({ ...a, [k]: (a[k] || 0) + 1 }), {});
  const done = moment?.target && Object.keys({ ...moment.target, ...counts }).every((k) => (moment.target[k] || 0) === (counts[k] || 0));
  useEffect(() => { if (done) sfx.success(); }, [done]);

  const pick = (k) => { setKey(k); setSlices(empty()); setHistory([]); };
  const drop = (k) => {
    if (slices[k].length >= MAX_PER_SLICE) { sfx.wrong(); return toast(z.full); }
    sfx.place();
    setHistory([...history, k]);
    setSlices(slices.map((s, i) => (i === k ? [...s, ing] : s)));
  };
  const undo = () => {
    const k = history[history.length - 1];
    if (k === undefined) return;
    sfx.remove();
    setHistory(history.slice(0, -1));
    setSlices(slices.map((s, i) => (i === k ? s.slice(0, -1) : s)));
  };
  const chip = (testid, onClick, active, children) => (
    <button key={testid} data-testid={testid} onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs md:text-sm font-semibold transition-colors duration-200 ${
        active ? "bg-[#2E5A3B] text-white" : "bg-white text-[#132A1D] border border-black/10 hover:bg-[#2E5A3B]/10"}`}>
      {children}
    </button>
  );

  return (
    <div data-testid="pizza-page" className="pt-16 min-h-screen bg-[#FBEFDD]">
      <Seo data={{ path: "/pizza", title: z.seoTitle, description: z.seoDesc, image: PAGES.pizza.image, jsonld: pageJsonld("pizza") }} />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 md:py-20">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#C86A4B] font-bold mb-3">{g.kicker}</p>
            <h1 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#132A1D] tracking-tight">{z.title}</h1>
            <p className="text-[#5C5C5C] mt-4 max-w-xl leading-relaxed">{z.sub}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href="/juegos/pizza/" data-testid="game-cards-page-link" className="inline-flex items-center gap-2 rounded-full border border-black/15 text-[#132A1D] px-6 py-3 text-sm font-semibold hover:bg-white transition-colors duration-300">{g.cards}</a>
            <Link to="/catalogo/pizza-enroscable" data-testid="game-buy-link" className="inline-flex items-center gap-2 rounded-full bg-[#C86A4B] text-white px-6 py-3 text-sm font-semibold hover:bg-[#2E5A3B] transition-colors duration-300">{z.buy} <ArrowRight size={16} /></Link>
          </div>
        </div>

        <GameOffer slug="pizza-enroscable" playPath="/pizza" />

        <div className="flex flex-wrap gap-2 mb-8" data-testid="game-modes">
          {chip("mode-free", () => pick(null), !moment, g.free)}
          {moments.map((m) => chip(`mode-${m.key}`, () => pick(m.key), key === m.key, `${m.serie} · ${m.name}`))}
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-6 relative">
            <Pizza svgRef={svgRef} slices={slices} onSlice={drop} hoverSlice={hover} onHover={setHover} className="w-full max-w-md mx-auto rounded-2xl shadow-[0_10px_40px_-15px_rgba(120,70,20,.35)]" />
            {done && (
              <div data-testid="game-success" className="absolute inset-x-4 top-4 bg-[#2E5A3B] text-white rounded-2xl px-6 py-4 flex items-center gap-3 shadow-lg animate-in fade-in">
                <Sparkles size={22} className="text-[#F6C744]" />
                <div><p className="font-serif-d text-2xl font-semibold leading-tight">{z.success}</p><p className="text-sm text-white/80">{z.successSub}</p></div>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
              <p className="text-xs text-[#5C5C5C]" data-testid="pizza-count">{slices.flat().length} {z.toppings} · {z.tip}</p>
              <div className="flex flex-wrap items-center gap-2"><SoundToggle /><SaveToGallery svgRef={svgRef} caption={moment ? `${moment.serie}: ${moment.name}` : g.free} game="pizza" bg="#FBEFDD" disabled={!slices.flat().length} /><ShareButtons svgRef={svgRef} caption={moment ? `${moment.serie}: ${moment.name}` : g.free} file="mi-pizza-pame3d.png" url="https://pame3d.com/pizza" g={g} bg="#FBEFDD" /></div>
            </div>
          </div>

          <div className="lg:col-span-6 space-y-6">
            {moment && (
              <div className="bg-white rounded-2xl p-5 border border-black/5 grid sm:grid-cols-2 gap-5" data-testid="game-target">
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#C86A4B] font-bold">{moment.serie}</p>
                  <p className="font-serif-d text-2xl font-semibold text-[#132A1D] mt-1" data-testid="game-target-name">{moment.name}</p>
                  <p className="text-xs text-[#5C5C5C] mt-3 mb-2">{moment.target ? z.needs : z.freeMoment}</p>
                  <ul className="space-y-1" data-testid="pizza-target-list">
                    {moment.ingredients.map((s, i) => {
                      const name = s.split(" × ")[0];
                      const need = moment.target?.[name], have = counts[name] || 0;
                      const ok = need !== undefined && have === need;
                      return <li key={i} className={`text-sm flex items-center justify-between gap-2 ${ok ? "text-[#2E5A3B] font-semibold" : have > (need ?? Infinity) ? "text-[#C86A4B]" : "text-[#132A1D]"}`}><span>{s}</span>{need !== undefined && <span data-testid={`pizza-have-${name.toLowerCase().replace(/[^a-z]/g, "")}`}>{have}/{need}</span>}</li>;
                    })}
                  </ul>
                  <p className="text-xs text-[#5C5C5C] mt-4 leading-relaxed"><b>{z.works}</b> {moment.works}</p>
                </div>
                <div>
                  <img src={moment.img} alt={moment.name} loading="lazy" className="w-full aspect-square object-cover rounded-xl" />
                  <ul className="text-xs text-[#5C5C5C] mt-3 space-y-1 list-decimal pl-4">{moment.rules.map((r, i) => <li key={i}>{r}</li>)}</ul>
                </div>
              </div>
            )}
            <div className="bg-white rounded-2xl p-4 border border-black/5">
              <div className="flex flex-wrap gap-2 mb-4">
                {chip("game-undo", undo, false, <><Undo2 size={14} /> {g.undo}</>)}
                {chip("game-clear", () => { setSlices(empty()); setHistory([]); }, false, <><Trash2 size={14} /> {g.clear}</>)}
              </div>
              <div className="grid grid-cols-5 gap-2" data-testid="pizza-tray">
                {PIZZA_INGREDIENTS.map((k) => (
                  <button key={k} data-testid={`ing-${k.toLowerCase().replace(/[^a-z]/g, "")}`} onClick={() => { setIng(k); sfx.select(); }}
                    className={`rounded-xl p-2 flex flex-col items-center gap-1 transition-colors duration-200 ${ing === k ? "bg-[#F6C744]/50 ring-2 ring-[#2E5A3B]" : "bg-[#FBEFDD] hover:bg-[#F6C744]/30"}`}>
                    <ToppingIcon name={k} />
                    <span className="text-[11px] font-semibold text-[#132A1D]">{k}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
